import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { isNil } from 'lodash-es'

import { attendanceValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'

import { coachingSessionTypesSet } from '../../../common/constants/appConstants'
import { logToSentry } from '../../../common/utils/utils'
import { getHealthPlanICD10s } from '../../../data/healthPlan/healthPlanSelectors'
import { getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { useEapEligibilityError } from '../../../hooks/useEapEligibilityError'
import {
  CloseSessionEligibilityResult,
  useCloseSessionEligibility,
} from '../../sessions/endOfSession/hooks/useCloseSessionEligibility'
import { useCloseSessionSubmit } from '../../sessions/endOfSession/hooks/useCloseSessionSubmit'
import { useInitialSessionDetails } from '../../sessions/endOfSession/hooks/useInitialSessionDetails'
import { useModalityOptions } from '../../sessions/endOfSession/hooks/useModalityOptions'
import {
  FlexNoteFormValues,
  mapFlexibleNoteValuesToCloseSessionInput,
} from '../../sessions/endOfSession/mapFlexibleNoteValuesToCloseSessionInput'

type UseFlexibleNoteCloseSessionParams = {
  isFlexibleNote: boolean
  appointmentToClose: Appointment | undefined
  selectedEpisode: Episode | undefined
  /**
   * The note's already-saved form values (an existing/draft note's `body`). Used to seed the session
   * rate/flags on load — before the form fires its first onChange — so a reopened note reflects its
   * saved modality/attendance rather than reverting to the appointment default.
   */
  savedNoteValues?: FlexNoteFormValues
}

export type FlexibleNoteCloseSessionAbortReason = 'missingNoteId' | 'closeSessionError'

export type FlexibleNoteCloseSessionResult = {
  /** Charge id created by closing the session; null when no session was closed. */
  chargeId: string | null
  /** True only when the created charge is `pending_note` — i.e. it requires an accept-charge step. */
  isPendingNoteCharge: boolean
  /** True when a charge creation was actually attempted, regardless of outcome. */
  chargeAttempted: boolean
  isCancellationLimitExceeded: boolean
  /** True when the close-session step failed and the note submit should be aborted. */
  aborted: boolean
  /** Set when `aborted` is true, so the caller can tailor the error message. */
  abortReason?: FlexibleNoteCloseSessionAbortReason
  /** Server-provided error message when abortReason is 'closeSessionError'; undefined otherwise. */
  errorMessage?: string
}

// The close-session answers the flexible note form collects that influence eligibility/billing.
type FlexCloseSessionAnswers = {
  modality?: string
  attendance?: string
  changeReason?: string
  changeNotice?: string
}

/**
 * The flexible-note form fields whose values feed eligibility/session-rate derivation. These are the
 * only fields whose change should reinitialize the note form (so the derived session rate re-renders);
 * changes to any other field don't affect the schema and shouldn't trigger a reinitialize. Shared with
 * `NoteContainer` via the `reinitializeOnFieldsChange` prop so the two stay in sync.
 */
export const FLEX_NOTE_RATE_FIELDS: Array<keyof FlexCloseSessionAnswers> = [
  'modality',
  'attendance',
  'changeReason',
  'changeNotice',
]

/**
 * Owns the entire close-session concern of the consolidated DA Flexible Note so `ClientNoteDetails`
 * can stay a generic note surface. It:
 *   - tracks the close-session answers from the live note form (`onValuesChange`),
 *   - derives the not-attended / late-cancellation flags (same as EndOfSession),
 *   - builds session-rate/health-plan eligibility (`useCloseSessionEligibility`),
 *   - exposes `showHealthPlanIcd10` (the ICD-10 capture gate), and
 *   - exposes `closeSession`, which maps the form values and closes the session before the note saves.
 *
 * `closeSession` is a no-op (`{ chargeId: null, isPendingNoteCharge: false, chargeAttempted: false, isCancellationLimitExceeded: false, aborted: false }`)
 * for non-flexible notes or non-`completed` submits; `eligibility` is inert for non-flexible notes.
 */
export const useFlexibleNoteCloseSession = ({
  isFlexibleNote,
  appointmentToClose,
  selectedEpisode,
  savedNoteValues,
}: UseFlexibleNoteCloseSessionParams) => {
  const icd10s = useSelector(getHealthPlanICD10s)
  const clientId = useSelector(getSelectedClientId)

  const eligibilityError = useEapEligibilityError({
    programName: selectedEpisode?.program_name,
    isEnabled: isFlexibleNote,
  })

  // Live close-session answers, mirrored into state so eligibility (session rate/type) recomputes as
  // the provider fills them in — the same inputs EndOfSession derives from its form values.
  const [answers, setAnswers] = useState<FlexCloseSessionAnswers>({})

  const { modality: initialModality } = useInitialSessionDetails(appointmentToClose, useModalityOptions().enum)

  // `answers` is empty until the form fires its first onChange, so the rate/flags would otherwise be
  // computed with no modality (base rate) on load. Fall back to the note's already-saved values (a
  // reopened draft), then to the appointment's default modality (a new note), so the rate reflects the
  // selected modality immediately instead of reverting to the base rate / appointment default.
  const effectiveModality = answers.modality ?? savedNoteValues?.modality ?? initialModality
  const effectiveAttendance = answers.attendance ?? savedNoteValues?.attendance
  const effectiveChangeReason = answers.changeReason ?? savedNoteValues?.changeReason
  const effectiveChangeNotice = answers.changeNotice ?? savedNoteValues?.changeNotice

  const isNonAttendedSession = effectiveAttendance === attendanceValues.NO_SHOW
  const isLateClientCancellation =
    effectiveChangeReason === 'clientInitiatedLate' ||
    (effectiveChangeReason === 'clientCancel' && effectiveChangeNotice === 'clientInitiatedLate')

  const eligibility: CloseSessionEligibilityResult = useCloseSessionEligibility({
    appointment: isFlexibleNote ? appointmentToClose : undefined,
    episode: isFlexibleNote ? selectedEpisode : undefined,
    modality: effectiveModality,
    isNonAttendedSession,
    isLateClientCancellation,
  })

  const closeSessionSubmit = useCloseSessionSubmit({
    appointmentToClose,
    selectedEpisode,
    originallyTargetedAppointment: null,
    sessionDetails: eligibility.sessionDetails,
    isUsingSelfPay: eligibility.isUsingSelfPay,
    shouldShowGuardianConsent: eligibility.shouldShowGuardianConsent,
    programConfig: eligibility.programConfig,
    // DA flexible note always captures enhanced diagnosis (primary/secondary condition)
    useEnhancedDiagnosisCapture: true,
    isDaFlexibleNote: true,
  })

  // ICD-10 capture is required when the next session bills a health plan or the client self-pays,
  // for non-coaching session types — mirrors the EndOfSession gate.
  const sessionType = eligibility.sessionDetails?.sessionType
  const isNotCoachingTypeSession = !isNil(sessionType) && !coachingSessionTypesSet.has(sessionType)
  const nextSessionUsesHealthPlan = Boolean(eligibility.eligibilityObj?.next_session_uses_health_plan)
  const showHealthPlanIcd10 =
    isFlexibleNote &&
    ((nextSessionUsesHealthPlan && isNotCoachingTypeSession) ||
      (eligibility.isUsingSelfPay && (isNotCoachingTypeSession || isNil(sessionType))))

  // Wire into the note form's onChange; only the answers that affect eligibility are tracked, and we
  // bail when nothing relevant changed to avoid extra renders.
  const onValuesChange = useCallback(
    (values: FlexNoteFormValues) => {
      if (!isFlexibleNote) {
        return
      }
      setAnswers((prev) => {
        const next: FlexCloseSessionAnswers = {
          modality: values?.modality,
          attendance: values?.attendance,
          changeReason: values?.changeReason,
          changeNotice: values?.changeNotice,
        }
        return FLEX_NOTE_RATE_FIELDS.every((field) => prev[field] === next[field]) ? prev : next
      })
    },
    [isFlexibleNote],
  )

  const closeSession = useCallback(
    async ({
      values,
      status,
      noteId,
    }: {
      values: FlexNoteFormValues
      status: string
      noteId: string | undefined
    }): Promise<FlexibleNoteCloseSessionResult> => {
      if (!isFlexibleNote || status !== 'completed' || !appointmentToClose) {
        return {
          chargeId: null,
          isPendingNoteCharge: false,
          chargeAttempted: false,
          isCancellationLimitExceeded: false,
          aborted: false,
        }
      }

      if (!noteId) {
        logToSentry('daFlexibleNoteWorkflow', {
          message: 'Aborting close session: no note id, refusing to create a charge without an existing note',
          appointmentId: appointmentToClose.appointmentId,
          clientId,
        })
        return {
          chargeId: null,
          isPendingNoteCharge: false,
          chargeAttempted: false,
          isCancellationLimitExceeded: false,
          aborted: true,
          abortReason: 'missingNoteId',
        }
      }

      try {
        // Map the DA Flex Note values into the shape the end-of-session flow expects, then close.
        const closeSessionInput = mapFlexibleNoteValuesToCloseSessionInput(values, icd10s?.all)
        const closeResult = await closeSessionSubmit.submitForm(closeSessionInput)

        if (closeResult?.hasNoShowTransfersError) {
          logToSentry('daFlexibleNoteWorkflow', {
            message: 'Close session returned no-show transfers error',
            appointmentId: appointmentToClose.appointmentId,
            clientId,
          })
        }

        return {
          chargeId: closeResult?.chargeId ?? null,
          isPendingNoteCharge: closeResult?.isPendingNoteCharge ?? false,
          chargeAttempted: closeResult?.chargeAttempted ?? false,
          isCancellationLimitExceeded: closeResult?.isCancellationLimitExceeded ?? false,
          aborted: false,
        }
      } catch (error) {
        logToSentry('daFlexibleNoteWorkflow', {
          message: 'Error during close session submit in unified flow',
          error: error instanceof Error ? { message: error.message, stack: error.stack } : { error },
          appointmentId: appointmentToClose.appointmentId,
          clientId,
        })
        const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        return {
          chargeId: null,
          isPendingNoteCharge: false,
          chargeAttempted: false,
          isCancellationLimitExceeded: false,
          aborted: true,
          abortReason: 'closeSessionError',
          errorMessage,
        }
      }
    },
    [isFlexibleNote, appointmentToClose, icd10s, closeSessionSubmit, clientId],
  )

  return { eligibility, closeSession, showHealthPlanIcd10, onValuesChange, eligibilityError }
}
